/* eslint-disable no-unused-vars */
import React, { useRef, memo, useEffect } from 'react';
import Panoramax from '@panoramax/web-viewer';
import '@panoramax/web-viewer/build/index.css';
import { config } from '../config';

const testsAreEquals = (prevRoad, nextRoad) =>
  prevRoad.idView === nextRoad.idView;

const StreetViewPanoramaxContainer = ({ idView }) => {
  const containerRef = useRef(null);

  const style = {
    viewer: {
      width: '300px',
      height: '300px',
    },
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    //  removed from tab order
    const disableFocusables = (root) => {
      if (!(root instanceof HTMLElement)) {
        return;
      }
      // match common interactive elements
      root
        .querySelectorAll('a, button, [tabindex], input, select, textarea')
        .forEach((node) => {
          try {
            node.setAttribute('tabindex', '-1');
            node.setAttribute('aria-hidden', 'true');
            node.setAttribute('role', 'presentation');
          } catch (e) {
            // ignore nodes we cannot modify
          }
        });
      // also if the root itself is interactive
      if (
        root.matches &&
        root.matches('a, button, [tabindex], input, select, textarea')
      ) {
        try {
          root.setAttribute('tabindex', '-1');
          root.setAttribute('aria-hidden', 'true');
          root.setAttribute('role', 'presentation');
        } catch (e) {
          console.error('error check focus panoramax', e);
        }
      }
    };

    // Initial pass in case some nodes already exist
    disableFocusables(container);

    // MutationObserver to catch injected controls after viewer initialization
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        // handle added nodes
        for (const node of m.addedNodes) {
          if (!(node instanceof HTMLElement)) {
            continue;
          }
          disableFocusables(node);
          // also scan subtree
          node.querySelectorAll && disableFocusables(node);
        }
      }
    });

    observer.observe(container, { childList: true, subtree: true });

    // Make container non-focusable / ignored by screen readers if you provide an accessible fallback elsewhere.
    try {
      container.setAttribute('aria-hidden', 'true');
      // do not use inert because we still want to interact with image
    } catch (e) {
      console.error('error set attribute hidden');
    }

    return () => {
      observer.disconnect();
      try {
        container.removeAttribute('aria-hidden');
        container.inert = false;
      } catch (e) {
        console.error('error remove observer view panoramax');
      }
      // remove any tabindex=-1 we set (optional, can be omitted)
      try {
        container
          .querySelectorAll('[tabindex="-1"]')
          .forEach((n) => n.removeAttribute('tabindex'));
        container
          .querySelectorAll('[aria-hidden="true"]')
          .forEach((n) => n.removeAttribute('aria-hidden'));
        container
          .querySelectorAll('[role="presentation"]')
          .forEach((n) => n.removeAttribute('role'));
      } catch (e) {
        console.error('error remove observer tab index panoramax');
      }
    };
  }, [idView]);

  return (
    <div ref={containerRef}>
      <pnx-photo-viewer
        endpoint={`${config.url.GRAPH_PANORAMAX_URL}`}
        picture={idView.id}
        widgets='false'
        url-parameters='false'
        keyboard-shortcuts='false'
        style={style.viewer}
      />
    </div>
  );
};

export const StreetViewPanoramax = memo(
  StreetViewPanoramaxContainer,
  testsAreEquals,
);
